<template>
  <!-- In-app chat with the consumer (Android ChatFragment / item_chat.xml) — Firestore chat/{inbox.id}/messages -->
  <Teleport to="body">
    <div ref="modal" class="modal fade fxModal chatWindow" tabindex="-1" aria-hidden="true" data-bs-backdrop="static"
      data-bs-keyboard="false">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 560px">
        <div class="modal-content">
          <div class="modal-body p-[20px] font-roboto flex flex-col !overflow-hidden">
            <FxModalHeader :title="title" :dismiss="false" @close="close" />

            <!-- messages: the only scrolling area so the composer stays pinned at the bottom -->
            <div ref="list" class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-[14px] pr-[4px] !px-[10px] tablet:!px-0">
              <p v-if="error" class="m-auto text-center text-[14px] text-[#D7385E]">{{ error }}</p>
              <p v-else-if="loading" class="m-auto text-center text-[14px] text-[#999999]">Loading messages…</p>
              <EmptyState v-else-if="!ordered.length" title="No messages yet" text="" min-height="m-auto" />
              <!-- avatar, time and the bubble's pointed corner at the top of each message -->
              <div v-for="msg in ordered" :key="msg.id" class="flex items-start gap-[8px]"
                :class="isMine(msg) ? 'flex-row-reverse' : ''">
                <img :src="avatar(msg)" alt="" class="w-[32px] h-[32px] rounded-full object-cover flex-shrink-0 bg-[#EFEFEF]">
                <div class="flex flex-col max-w-[75%]" :class="isMine(msg) ? 'items-end' : 'items-start'">
                  <span class="mb-[4px] text-[11px] text-[#999999]">{{ formatTime(msg.date) }}</span>
                  <div class="px-[12px] py-[8px] text-[14px] leading-[20px] whitespace-pre-line break-words"
                    :class="isMine(msg)
                      ? 'bg-[#7695FF] text-white rounded-[12px] rounded-tr-[2px]'
                      : 'bg-white text-[#333333] border-1 border-[#E1E5EF] rounded-[12px] rounded-tl-[2px]'">
                    <!-- links become real <a> tags; the rest stays plain text (no v-html: consumers write these) -->
                    <template v-for="(part, i) in $helper.linkParts(msg.message)" :key="i">
                      <a v-if="part.url" :href="part.url" target="_blank" rel="noopener noreferrer"
                        class="underline break-all" :class="isMine(msg) ? 'text-[#fff] hover:text-[#fff]' : 'text-[#7695FF] hover:text-[#5B7BEA]'">{{ part.text }}</a>
                      <template v-else>{{ part.text }}</template>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- composer (Android: hidden when status == 0 or >= 9, except 16) -->
            <!-- Enter sends, Shift+Enter adds a line; the box grows with the text up to 120px, then scrolls -->
            <form v-if="canSend" class="flex items-end gap-[10px] pt-[15px] mt-[10px] border-t-1 border-[#EFEFEF] px-[10px] tablet:px-0 pb-[10px] tablet:pb-0"
              @submit.prevent="sendMessage">
              <textarea ref="input" v-model="text" rows="1" placeholder="Type a message…" autocomplete="off" :disabled="!ready"
                class="flex-1 min-w-0 min-h-[40px] max-h-[120px] px-[15px] py-[9px] rounded-[20px] border-1 border-[#E1E5EF] text-[14px] leading-[20px] outline-none resize-none focus:border-[#00B7C2] bg-white"
                @keydown.enter.exact.prevent="sendMessage" />
              <button type="submit" class="fxBtnPrimary !rounded-full !min-h-[40px] !h-[40px] !px-[18px]"
                :disabled="!ready || sending || !text.trim()" aria-label="Send message">
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#FFFFFF" d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z" />
                </svg>
                Send
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
import FxModalHeader from "../nest_components/FxModalHeader.vue";
import EmptyState from "../partialComponent/EmptyState.vue";
import { inboxApi } from "../../services/inboxApi";
import { ensureSignedIn, listen, send, myUserId } from "../../services/firebaseChat";

const toast = useToast();
const DEFAULT_AVATAR = "https://t3.ftcdn.net/jpg/03/46/83/96/360_F_346839683_6nAPzbhpSkIpb8pmAwufkC7c5eD7wYws.jpg";
const pad = (n) => String(n).padStart(2, "0");

export default {
  components: { FxModalHeader, EmptyState },
  emits: ["closed"],
  data() {
    return {
      inbox: null,
      messages: [],
      text: "",
      loading: false,
      ready: false,
      sending: false,
      error: "",
      lastMessage: "",
      sentAny: false,
      me: 0,
    };
  },
  computed: {
    title() {
      return this.inbox?.consumer?.full_name || "Chat";
    },
    // Firestore returns newest first; show oldest at top, newest at the bottom
    ordered() {
      return [...this.messages].reverse();
    },
    canSend() {
      const status = Number(this.inbox?.status);
      return !!this.inbox && (status === 16 || !(status === 0 || status >= 9));
    },
  },
  watch: {
    "messages.length"() {
      this.scrollToBottom();
    },
    // grow the composer with its lines (also shrinks back after send clears it)
    text() {
      this.$nextTick(() => {
        const el = this.$refs.input;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight + 2, 120) + "px";
      });
    },
  },
  mounted() {
    this.$refs.modal.addEventListener("hidden.bs.modal", this.onHidden);
  },
  beforeUnmount() {
    this.$refs.modal?.removeEventListener("hidden.bs.modal", this.onHidden);
    this.cleanup();
    // leaving the page with the chat open: drop Bootstrap's backdrop / body lock too
    if (this.$refs.modal?.classList.contains("show")) {
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
    }
    this.modal?.dispose();
  },
  methods: {
    // open({ bookingId }) | open({ orderId }) | open({ inbox })
    async open({ bookingId, orderId, inbox } = {}) {
      this.cleanup();
      this.reset();
      if (inbox) {
        this.inbox = inbox;
      } else {
        const res = await inboxApi.init({ bookingId, orderId });
        if (!res.ok || !res.body?.id) {
          toast.error(res.message || "Unable to open the chat. Please try again.");
          return false;
        }
        this.inbox = res.body;
      }
      this.me = myUserId();
      this.modal = this.modal || new Modal(this.$refs.modal);
      this.modal.show();
      this.connect();
      return true;
    },
    close() {
      this.modal?.hide();
    },
    reset() {
      Object.assign(this, { inbox: null, messages: [], text: "", loading: false, ready: false, sending: false, error: "", lastMessage: "", sentAny: false });
    },
    async connect() {
      const inboxId = this.inbox.id;
      this.loading = true;
      try {
        await ensureSignedIn();
        if (!this.inbox || this.inbox.id !== inboxId) return; // closed / switched meanwhile
        this.unsubscribe = listen(inboxId, (list) => {
          this.messages = list;
          this.loading = false;
        }, () => {
          this.loading = false;
          this.error = "Unable to load messages. Please try again.";
        });
        this.ready = true;
        this.$nextTick(() => this.$refs.input?.focus());
      } catch (e) {
        this.loading = false;
        this.error = e?.message || "Unable to connect to chat. Please try again.";
      }
    },
    async sendMessage() {
      const value = this.text.trim();
      if (!value || !this.ready || this.sending) return;
      this.sending = true;
      try {
        const sent = await send(this.inbox, value);
        if (sent) {
          this.text = "";
          this.lastMessage = sent;
          this.sentAny = true;
        }
      } catch (e) {
        toast.error("Message could not be sent. Please try again.");
      } finally {
        this.sending = false;
        this.$nextTick(() => this.$refs.input?.focus());
      }
    },
    // stop listening and store the last message on the inbox row (Android UpdateInbox on pause)
    cleanup() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      let saved = Promise.resolve();
      if (this.lastMessage && this.inbox) {
        saved = inboxApi.update(this.inbox.id, this.lastMessage);
        this.lastMessage = "";
      }
      return saved;
    },
    async onHidden() {
      const sent = this.sentAny;
      const inbox = this.inbox;
      const saved = this.cleanup();
      this.reset();
      await saved; // so a list reload sees the new last message
      this.$emit("closed", { sent, inbox });
    },
    isMine(msg) {
      return Number(msg.sender) === this.me;
    },
    avatar(msg) {
      const who = this.isMine(msg) ? "technician" : "consumer";
      return this.inbox?.[who]?.profile_picture || msg.inbox?.[who]?.profile_picture || DEFAULT_AVATAR;
    },
    // Android DateTimeHelper.getChatDateTime: "h:mm a" today, otherwise date + "h:mm a"
    formatTime(date) {
      if (!date) return "";
      const h = date.getHours();
      const time = `${h % 12 || 12}:${pad(date.getMinutes())} ${h < 12 ? "AM" : "PM"}`;
      if (date.toDateString() === new Date().toDateString()) return time;
      return `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()} ${time}`;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.list;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style>
/* fixed-height chat: the message list scrolls, header and composer stay put */
.modal.fxModal.chatWindow .modal-content {
  height: min(720px, calc(100vh - 32px));
}
</style>
